import { Chip, GridLegacy as Grid, Stack, Typography } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { RelativeTimestamp } from 'components/core/time/display'
import { AppUserDisplayNameWithFetch } from 'components/pages/app/users/app-user-display-name'
import { ResourceDisplayName } from 'components/pages/entitlement/resource/ResourceDisplayName'
import { UserDisplayNameWithFetch } from 'components/pages/user/user-display-name'
import { InventoryAlertType, InventoryAlertStatus } from 'pbts/c1/api/inventory/v1/inventory_alerts'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Fragment, ReactElement } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { HistoricalInventoryAlertValue, InventoryAlertValue } from 'redux_toolkit/apps'
import { getDisplayAlert } from './alert-util'
import { InventoryAlertStrength } from './InventoryAlertStrength'

type InventoryAlertDetailsProps = {
    expandedAlert: InventoryAlertValue | HistoricalInventoryAlertValue,
}

export const InventoryAlertDetails = ({ expandedAlert }: InventoryAlertDetailsProps): ReactElement | null => {
    const alert = expandedAlert?.inventoryAlert
    const intl = useIntl()
    if (!alert) {
        return null
    }
    const alertDetails = getDisplayAlert(intl, expandedAlert)
    if (!alertDetails) {
        return null
    }

    const resource = expandedAlert.resource
    const metadata = getMetadata({ expandedAlert, intl })

    return <Stack spacing={3}>
        <Stack spacing={2}>
            <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={alert.data?.status === InventoryAlertStatus.RESOLVED ? { color: 'grey' } : {}}
                >
                    {alertDetails.title}
                </Typography>
                {alert.data?.severity && <InventoryAlertStrength severity={alert.data.severity} />}
                {alert.data?.status === InventoryAlertStatus.RESOLVED && (
                    <Chip
                        color="info"
                        label={intl.formatMessage({
                            id: 'inventory_alert_status_resolved',
                            defaultMessage: 'Resolved',
                        })}
                    />
                )}
                {alert.data?.status === InventoryAlertStatus.ACTIVE && (
                    <Chip
                        color="success"
                        label={intl.formatMessage({
                            id: 'inventory_alert_status_active',
                            defaultMessage: 'Open',
                        })}
                    />
                )}
            </Stack>
            <Typography color="text.secondary" sx={alert.data?.status === InventoryAlertStatus.RESOLVED ? { color: 'grey.500' } : {}}>
                {alertDetails.description}
            </Typography>
        </Stack>
        {resource && <ResourceDisplayName
            resource={resource}
            resourceType={expandedAlert?.appResourceType}
            appName={expandedAlert?.app?.displayName}
            includeDetails
        />}
        {expandedAlert.appUser &&
            <AppUserDisplayNameWithFetch id={expandedAlert.appUser.id} appId={expandedAlert.appUser.appId} />}
        <Stack spacing={2}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                        <FormattedMessage id="inventory_alert_detected_on" defaultMessage="Detected On" />
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography>{alertDetails.detectedOn}</Typography>
                </Grid>
                {metadata?.map((m, i) => (
                    <Fragment key={i}>
                        <Grid item xs={6} sx={{ display: 'flex', minHeight: '32px', alignItems: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary">{m.label}</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ display: 'flex', minHeight: '32px', alignItems: 'center' }}>
                            {m.value}
                        </Grid>
                    </Fragment>
                ))}
            </Grid>
        </Stack>
    </Stack>
}

type GetMetadataProps = {
    expandedAlert: InventoryAlertValue | HistoricalInventoryAlertValue, intl: IntlShape
}

type Metadata = {
    label: string,
    value: ReactElement,
}

const getMetadata = ({ expandedAlert, intl }: GetMetadataProps): Metadata[] | null => {
    const resource = expandedAlert.resource
    const secretTrait = resource?.metadata?.oneofKind === 'secretTrait' ? resource?.metadata?.secretTrait : undefined
    const alert = expandedAlert.inventoryAlert
    if (!alert) {
        return null
    }
    switch (alert.data?.type) {
        case InventoryAlertType.DEACTIVATED_ACCOUNT_OWNER:
            return expandedAlert.appUser?.identityUserId ? [{
                label: intl.formatMessage({
                    id: 'inventory_alert_deactivated_owner',
                    defaultMessage: 'Deactivated Owner',
                }),
                value: <UserDisplayNameWithFetch id={expandedAlert.appUser?.identityUserId} showUserStatus showAvatar={false} />,
            }] : []
        case InventoryAlertType.UNUSED_SECRET_API_TOKEN:
            return [{
                label: intl.formatMessage({
                    id: 'inventory_alert_secret_last_used',
                    defaultMessage: 'Secret Last Used at',
                }),
                value: secretTrait ? <RelativeTimestamp
                    date={secretTrait?.lastUsedAt ? Timestamp.toDate(secretTrait?.lastUsedAt) : undefined}
                    variant="date"
                /> : <C1EmptyIndicator />,
            }, {
                label: intl.formatMessage({
                    id: 'inventory_alert_secret_expires',
                    defaultMessage: 'Secret Expires At',
                }),
                value: secretTrait ? <RelativeTimestamp
                    date={secretTrait?.secretExpiresAt ? Timestamp.toDate(secretTrait?.secretExpiresAt) : undefined}
                    variant="date"
                /> : <C1EmptyIndicator />,
            }]
        default:
            return []
    }
}
